/**
 * Pump component — adds pressure head to the flow.
 *
 * Three modes:
 *   fixed-flow: Fixed mass flow rate, pressure rise from rated values
 *   curve-based: H-Q curve. Behaviour depends on `pumpType`:
 *     - centrifugal (default): affinity laws (N4 calibration)
 *       H = (F·H_rated)·N² − ((F−1)·H_rated/Q_rated²)·Q², F = SHUTOFF_HEAD_FACTOR
 *       ⇒ H(Q_rated, 1) = H_rated, shutoff H(0, 1) = F·H_rated
 *       (flow FALLS as discharge head rises — a shallow, dropping H-Q curve)
 *     - positive-displacement: near-CONSTANT volumetric flow vs head
 *       (a near-VERTICAL H-Q curve), scaled by speed, capped at a
 *       pressure/power limit. The pump delivers a fixed swept volume per
 *       revolution; flow is set by speed, NOT by the system head.
 *   dynamic-speed: Rotational inertia: J·dN/dt = T_motor - T_pump
 *
 * PD volumetric-efficiency / slip assumption (ENGINEERING ASSUMPTION — see
 * flagsForUser): real PD pumps lose a small fraction of theoretical flow to
 * internal back-leakage ("slip") that grows roughly linearly with the
 * differential pressure. We model this with a volumetric efficiency
 *     η_v = volEffNominal − slipCoeff × (ΔP / ΔP_max)
 * clamped to [volEffFloor, 1]. With the default slipCoeff = 0 the PD curve is
 * exactly vertical (flow independent of head) until the pressure cap; a small
 * non-zero slipCoeff tilts it slightly, as observed on real gear/screw pumps
 * (Karassik et al., "Pump Handbook", 4th ed., §3 positive-displacement pumps).
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { vaporPressureFromPH_SI } from '../core/fluidAdapter';

const G = 9.80665;

/**
 * Shutoff-head factor for the centrifugal H-Q curve (N4).
 *
 * The affinity-law curve is calibrated so that the pump delivers its RATED
 * head at its RATED flow (H(Q_rated, N=1) = H_rated), with the zero-flow
 * (shutoff) head at SHUTOFF_HEAD_FACTOR × H_rated:
 *
 *     H(Q, N) = (F·H_rated)·N² − ((F−1)·H_rated / Q_rated²)·Q²,  F = 1.25
 *
 * Check: H(Q_rated, 1) = 1.25·H_rated − 0.25·H_rated = H_rated ✓
 *        H(0, 1)       = 1.25·H_rated (typical centrifugal shutoff rise
 *        of 10–40% above rated head; 25% is a representative mid value —
 *        Karassik et al., "Pump Handbook", centrifugal H-Q characteristics).
 *
 * The PREVIOUS curve  H = H_rated·N² − (H_rated/Q_rated²)·Q²  gave ZERO head
 * at rated flow — a pump that cannot pump at its own design point.
 */
export const SHUTOFF_HEAD_FACTOR = 1.25;

/**
 * The single centrifugal H-Q curve (N4 calibration), shared by the
 * curve-based AND dynamic-speed branches (C-F5: the dynamic branch previously
 * used H = H_rated·N² with NO −k·Q² droop, so a spun-up pump presented a
 * flat, flow-independent head and the cooling loops solved to >13× design
 * flow — the network could only balance on pipe friction).
 *
 * @param ratedHead  H_rated [m] at (Q_rated, N = 1)
 * @param ratedFlow  Q_rated [kg/s]
 * @param N          normalized speed (1 = rated)
 * @param Q          operating mass flow [kg/s]
 * @returns developed head [m] (may be negative past runout; callers clamp ≥ 0)
 */
export function centrifugalHead(
  ratedHead: number,
  ratedFlow: number,
  N: number,
  Q: number,
): number {
  const k = (SHUTOFF_HEAD_FACTOR - 1) * ratedHead / (ratedFlow * ratedFlow);
  return SHUTOFF_HEAD_FACTOR * ratedHead * N * N - k * Q * Q;
}

/**
 * NPSH-available margin below which we flag a likely-cavitation risk.
 *
 * This is a PROXY threshold, NOT a true NPSHa < NPSHr cavitation criterion:
 * NPSH-REQUIRED is a pump-specific datasheet quantity we do not have (it varies
 * with flow and impeller geometry). When NPSHa falls below this small margin the
 * suction static head is barely above the fluid's vapour pressure, which is the
 * regime where cavitation becomes likely on essentially any pump. See the
 * npshRequired flag in the structured report.
 */
const CAVITATION_MARGIN_M = 0.5; // m of head

/** Normalise the pumpType param to the two canonical kinds. */
function normalizePumpType(raw: unknown): 'centrifugal' | 'positive-displacement' {
  if (raw === 'positive-displacement' || raw === 'PD' || raw === 'pd') {
    return 'positive-displacement';
  }
  // centrifugal | undefined | anything else → centrifugal (preserves legacy behaviour)
  return 'centrifugal';
}

export class NSPump implements NSComponent {
  id: string;
  type = 'pump';
  params: Record<string, any>;
  private actualSpeed: number = 0; // rad/s for dynamic mode

  constructor(id: string, params: {
    mode?: 'fixed-flow' | 'curve-based' | 'dynamic-speed';
    pumpType?: 'centrifugal' | 'positive-displacement' | 'PD';
    ratedHead?: number;     // m
    ratedFlow?: number;     // kg/s
    ratedSpeed?: number;    // RPM
    speed?: number;         // 0-1 normalized
    efficiency?: number;    // 0-1 (hydraulic/overall — work convention, divide by eta)
    inertia?: number;       // kg·m² for dynamic mode
    mdot?: number;          // kg/s for fixed-flow
    rho?: number;           // kg/m³ reference density
    D?: number;             // m — pipe diameter for momentum source (default 0.1)
    // ── Positive-displacement parameters ──
    maxHead?: number;       // m — pressure/relief cap for the near-vertical PD curve
                            //     (default: 1.5 × ratedHead). Head is clamped here.
    volEffNominal?: number; // volumetric efficiency at zero ΔP (default 1.0 → no slip)
    slipCoeff?: number;     // slip slope: η_v drop per unit (ΔP/ΔP_max) (default 0 → vertical)
    volEffFloor?: number;   // floor on η_v so flow never collapses (default 0.5)
  }) {
    this.id = id;
    this.params = {
      mode: 'fixed-flow',
      pumpType: 'centrifugal',
      ratedHead: 50,
      ratedFlow: 50,
      ratedSpeed: 1500,
      speed: 1.0,
      efficiency: 0.8,
      inertia: 10,
      rho: 998,
      volEffNominal: 1.0,
      slipCoeff: 0,
      volEffFloor: 0.5,
      ...params,
    };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, dt: number): ComponentUpdate {
    const { mode, ratedHead, ratedFlow, ratedSpeed, speed, efficiency, inertia } = this.params;
    // Use actual inlet density if available from connected pipe, else fall back to reference
    const rho = this.params._inletRho ?? this.params.rho ?? 998;

    let deltaP = 0;
    let mdot: number | undefined;
    let W_pump = 0;

    if (mode === 'fixed-flow') {
      mdot = (this.params.mdot ?? ratedFlow) * speed;
      deltaP = ratedHead * rho * G * speed * speed;
      W_pump = mdot * deltaP / (rho * efficiency);
    } else if (mode === 'curve-based') {
      const pumpType = normalizePumpType(this.params.pumpType);

      if (pumpType === 'positive-displacement') {
        // ── Positive-displacement pump ────────────────────────────────────
        // A PD pump sweeps a fixed volume per revolution, so it delivers a
        // near-CONSTANT volumetric flow set by SPEED, essentially INDEPENDENT
        // of discharge head (a near-vertical H-Q curve). The system head is
        // whatever the downstream network demands, up to a pressure/relief cap.
        const N = speed;

        // Pressure/relief cap → maximum head this PD pump will push against.
        // Default 1.5 × ratedHead; head is clamped here (a real PD pump has a
        // relief valve to prevent unbounded pressure on a closed discharge).
        const maxHead = this.params.maxHead ?? 1.5 * ratedHead;

        // Head demanded by the system this iteration (from junction pressures
        // if available, else default to the rated operating head). Clamped to
        // the relief cap. This is the head the PD pump operates AGAINST; flow
        // does not follow it (that is the whole point of a PD pump).
        // System ΔP this iteration. The live SIMPLE solver calls
        // applyBoundaryConditions(junction, junction) so outlet===inlet and
        // outlet.P - inlet.P ≈ 0. When no real system ΔP is available, operate
        // at the pump's RATED head (its design point) rather than 0 — otherwise
        // the PD pump would impose no pressure rise and no work in the live
        // solve. _systemDeltaP (tests / future solver pressure-feedback) wins.
        const measuredDP = outlet && inlet ? Math.max(0, outlet.P - inlet.P) : 0;
        const dP_demand =
          this.params._systemDeltaP ??
          (measuredDP > 1 ? measuredDP : ratedHead * rho * G);
        const H = Math.min(maxHead, Math.max(0, dP_demand / (rho * G)));

        // Volumetric efficiency / slip model (ENGINEERING ASSUMPTION, flagged):
        //   η_v = volEffNominal − slipCoeff × (ΔP / ΔP_max), clamped to floor.
        // With slipCoeff = 0 (default) the curve is exactly vertical.
        const dPmax = Math.max(1, maxHead * rho * G);
        const dP_now = H * rho * G;
        const volEffNominal = this.params.volEffNominal ?? 1.0;
        const slipCoeff = this.params.slipCoeff ?? 0;
        const volEffFloor = this.params.volEffFloor ?? 0.5;
        const eta_v = Math.min(
          1,
          Math.max(volEffFloor, volEffNominal - slipCoeff * (dP_now / dPmax)),
        );

        // Constant volumetric delivery scaled by speed and reduced by slip.
        // ratedFlow (kg/s) at rated density defines the swept volume per unit
        // speed: Q_theo[m³/s] = (ratedFlow / rho) × N. Mass flow at the local
        // density = rho × Q_vol = ratedFlow × N × η_v.
        const Q_vol_theo = (ratedFlow / rho) * N;          // m³/s theoretical
        const Q_vol = Q_vol_theo * eta_v;                  // m³/s delivered
        mdot = rho * Q_vol;                                // kg/s — flow CONSTRAINT

        deltaP = Math.max(0, H * rho * G);
        // Adiabatic-with-efficiency work convention (divide by eta), same as
        // every other pump branch: W = ṁ·ΔP / (ρ·η).
        W_pump = mdot > 0 ? mdot * deltaP / (rho * efficiency) : 0;
        this.params._currentFlow = mdot;
        this.params._etaV = eta_v;
      } else {
        // ── Centrifugal pump (N4: recalibrated affinity-law curve) ────────
        // H(Q, N) = (F·H_rated)·N² − ((F−1)·H_rated/Q_rated²)·Q²,  F = 1.25
        // so H(Q_rated, N=1) = H_rated (rated duty at rated flow) and
        // H(0, N=1) = 1.25·H_rated (shutoff). The previous curve
        // H_rated·N² − (H_rated/Q_rated²)·Q² gave ZERO head at rated flow.
        const N = speed;
        // Use inlet mass flow (from connected pipe) if available, else rated × speed
        const Q_est = this.params._inletMdot ?? this.params._currentFlow ?? ratedFlow * N;
        const Q = Math.max(0.01, Q_est); // Prevent zero/negative flow
        const H = centrifugalHead(ratedHead, ratedFlow, N, Q);
        deltaP = Math.max(0, H * rho * G);
        W_pump = Q * deltaP / (rho * efficiency);
        this.params._currentFlow = Q; // Store for next iteration
      }
    } else if (mode === 'dynamic-speed') {
      // J·dω/dt = T_motor - T_hydraulic
      const omega_rated = ratedSpeed * 2 * Math.PI / 60; // RPM → rad/s
      const omega_target = speed * omega_rated;
      const omega = this.actualSpeed;
      const N_ratio = omega_rated > 0 ? omega / omega_rated : 0;

      // Motor torque: proportional control to reach target speed.
      // When tripped (speed=0, omega_target=0), motor torque is ZERO —
      // only hydraulic resistance decelerates the impeller (coast-down).
      let T_motor: number;
      if (speed <= 0 && omega_target <= 0) {
        T_motor = 0; // Tripped — no motor torque, free coast-down
      } else {
        // Proportional drive: moderate gain for stable acceleration
        T_motor = 500 * (omega_target - omega);
      }

      // C-F5 FIX: the dynamic-speed branch previously used the droop-free
      // H = H_rated·N² — a flat curve that let the cooling loops overshoot to
      // >13× design flow (nothing but pipe friction opposed it) and froze the
      // transient scenarios thermally. It now rides the SAME recalibrated
      // affinity curve as the curve-based branch (one curve family, N4):
      //     H(Q, N) = (F·H_rated)·N² − ((F−1)·H_rated/Q_rated²)·Q²
      // evaluated at the LIVE flow, so head droops as flow rises and the
      // operating point lands where the curve meets the system resistance.
      const Q_est = this.params._inletMdot ?? this.params._currentFlow ?? ratedFlow * N_ratio;
      const Q = Math.max(0, Q_est);
      const H = centrifugalHead(ratedHead, ratedFlow, N_ratio, Q);
      deltaP = Math.max(0, H * rho * G);
      W_pump = Q > 0 ? Q * deltaP / (rho * efficiency) : 0;
      this.params._currentFlow = Q; // store for the next iteration (parity with curve-based)

      // Hydraulic torque = P_hydraulic / ω (resists rotation)
      // Smooth transition near zero to avoid discontinuity
      const T_hydraulic = W_pump / Math.max(omega, 0.1);

      // Bearing friction torque — always present, proportional to speed.
      // Sized to dissipate ~2% of rated shaft power at rated speed.
      // FIX (R4, 2026-07-06 — C3 ride-through probe): rated hydraulic power on a
      // MASS-flow basis is G·H·ṁ/η (W). `ratedFlow` is kg/s (see @param + the
      // Q_vol = ratedFlow/rho conversion above), so the prior `rho *` factor was
      // spurious — it inflated the bearing torque ~1000×, which dominated the
      // angular-momentum balance and collapsed the trip coast-down to
      // sub-second (should be ~seconds for a real pump's inertia). Matches the
      // W_pump = Q·ΔP/(ρ·η) mass-basis power above.
      const omega_rated_abs = omega_rated > 0 ? omega_rated : 155; // rad/s fallback
      const P_rated_shaft = G * ratedHead * ratedFlow / efficiency; // W, mass basis (no rho)
      const T_bearing = 0.02 * P_rated_shaft / omega_rated_abs * (omega / omega_rated_abs);

      // Euler integration of angular momentum
      const dw = (T_motor - T_hydraulic - T_bearing) / inertia * dt;
      this.actualSpeed = Math.max(0, omega + dw);

      // Store actual speed for UI feedback
      this.params._actualSpeed = this.actualSpeed / omega_rated * (ratedSpeed / 60); // normalised 0-1 approx
    }

    // ── NPSH-available (READ-ONLY DERIVED OUTPUT) ─────────────────────────
    // Computed from the already-solved inlet state — it does NOT feed back into
    // deltaP/deltaH/mdot/W_pump or the solve loop in any way.
    //
    //   NPSHa = (P_suction − P_vapour) / (ρ·g) + v²/(2g)
    //
    // where:
    //   P_suction = inlet junction pressure [Pa] (live solver passes
    //               applyBoundaryConditions(junction, junction), so inlet === junction)
    //   P_vapour  = saturation pressure of the fluid at the inlet temperature [Pa],
    //               from the IAPWS-IF97 fluid adapter (boundary conversion lives there)
    //   ρ         = inlet liquid density [kg/m³] (same value the pump used above)
    //   v         = suction velocity = ṁ / (ρ·A) [m/s]
    //
    // NPSH-REQUIRED is a pump-datasheet quantity (varies with flow/impeller) that
    // we do NOT have, so we expose npshRequired = NaN and flag it. cavitationRisk
    // is therefore a LOW-NPSHa PROXY (NPSHa < CAVITATION_MARGIN_M), not NPSHa<NPSHr.
    const P_suction = inlet ? inlet.P : NaN;
    const h_inlet = inlet ? inlet.h : NaN;
    const area = this.params._area ?? (Math.PI / 4) * Math.pow(this.params.D ?? 0.1, 2);
    const mdotForVel = this.params._inletMdot ?? mdot ?? 0;

    let npshAvailable_m = NaN;
    let cavitationRisk = false;
    let npshVaporPressure_Pa = NaN;

    const P_vapor = Number.isFinite(P_suction) && Number.isFinite(h_inlet)
      ? vaporPressureFromPH_SI(P_suction, h_inlet)
      : null;

    if (
      P_vapor !== null &&
      Number.isFinite(P_suction) &&
      rho > 0 &&
      area > 0
    ) {
      npshVaporPressure_Pa = P_vapor;
      const staticHead_m = (P_suction - P_vapor) / (rho * G); // m (can be < 0 if P_suction < P_vapor)
      const v = mdotForVel / (rho * area); // m/s suction velocity
      const velocityHead_m = (v * v) / (2 * G); // m (always ≥ 0)
      npshAvailable_m = staticHead_m + velocityHead_m;
      // Low-NPSHa proxy for cavitation. NPSHa can be physically negative when the
      // suction pressure has dropped below vapour pressure — that is unambiguously
      // a cavitation regime, so the < margin test captures it.
      cavitationRisk = npshAvailable_m < CAVITATION_MARGIN_M;
    }

    const metadata = {
      W_pump,
      deltaP,
      head: deltaP / (rho * G),
      // Read-only NPSH outputs (NaN when fluid state / vapour pressure unavailable).
      npshAvailable_m,
      // 1 = cavitation risk flagged, 0 = not flagged (metadata is Record<string, number>).
      cavitationRisk: cavitationRisk ? 1 : 0,
      // NPSH-REQUIRED is unavailable without a pump datasheet — flagged as NaN.
      npshRequired_m: NaN,
      // Saturation (vapour) pressure used for NPSHa, for traceability (NaN if unavailable).
      npshVaporPressure_Pa,
    };
    this.params._lastMetadata = metadata;

    // ── N3: pump enthalpy rise ────────────────────────────────────────────
    // An adiabatic pump puts ALL of its shaft work into the fluid:
    //   Δh = w_shaft = ΔP / (ρ·η)     [Pa / (kg/m³) = J/kg]
    // The ideal part ΔP/ρ raises pressure head; the loss share
    // (1/η − 1)·ΔP/ρ dissipates into the fluid as heat — the small pump
    // temperature rise. The old `deltaH: 0` ("handled by EOS") was wrong:
    // nothing else added the work, so pumping was enthalpy-free.
    // Efficiency is guarded to (0, 1] with the same 0.8 fallback the
    // networkBuilder uses, so a corrupt η can never divide by zero.
    const eta_h = typeof efficiency === 'number' && efficiency > 0 && efficiency <= 1
      ? efficiency
      : 0.8;
    const deltaH = rho > 0 ? deltaP / (rho * eta_h) : 0;

    return {
      deltaP,
      deltaH,
      mdot,
      momentumSource: deltaP * area,
      // Shaft-work telemetry. NOT consumed by the network (pump energy
      // transfer rides on deltaH above) — see the ComponentUpdate contract.
      energySource: -W_pump,
      metadata,
    };
  }
}
